import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PriceAlertModal = ({ 
  isOpen = false, 
  onClose, 
  product = null,
  onSetAlert 
}) => {
  const [alertData, setAlertData] = useState({
    targetPrice: '',
    email: '',
    frequency: 'daily',
    vendors: 'all'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const frequencyOptions = [
    { value: 'instant', label: 'Instant notifications' },
    { value: 'daily', label: 'Daily digest' },
    { value: 'weekly', label: 'Weekly summary' }
  ];

  const vendorOptions = [
    { value: 'all', label: 'All vendors' },
    { value: 'preferred', label: 'Preferred vendors only' },
    { value: 'specific', label: 'Specific vendors' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onSetAlert({
        productId: product?.id,
        productName: product?.name,
        targetPrice: parseFloat(alertData?.targetPrice),
        email: alertData?.email,
        frequency: alertData?.frequency,
        vendors: alertData?.vendors,
        createdAt: new Date()?.toISOString()
      });

      // Reset form
      setAlertData({
        targetPrice: '',
        email: '',
        frequency: 'daily',
        vendors: 'all'
      });

      onClose();
    } catch (error) {
      console.error('Error setting price alert:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setAlertData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen || !product) return null;

  const currentPrice = product?.vendors?.[0]?.price || 0;
  const suggestedPrice = Math.floor(currentPrice * 0.9);

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Set Price Alert</h3>
            <p className="text-sm text-muted-foreground">
              Get notified when the price drops
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors interactive-scale"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Product Info */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start space-x-3">
            <img
              src={product?.image || '/assets/images/no_image.png'}
              alt={product?.name}
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-1">{product?.name}</h4>
              <p className="text-sm text-muted-foreground mb-2">
                Current price: <span className="font-semibold">${currentPrice}</span>
              </p>
              <div className="flex items-center space-x-2">
                <Icon name="TrendingDown" size={14} color="var(--color-success)" />
                <span className="text-xs text-success">
                  Suggested alert: ${suggestedPrice}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Target Price"
            type="number"
            placeholder={`e.g., ${suggestedPrice}`}
            value={alertData?.targetPrice}
            onChange={(e) => handleInputChange('targetPrice', e?.target?.value)}
            required
            description={`Current price is $${currentPrice}`}
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="your@email.com"
            value={alertData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            required
            description="We'll send alerts to this email"
          />

          <Select
            label="Notification Frequency"
            options={frequencyOptions}
            value={alertData?.frequency}
            onChange={(value) => handleInputChange('frequency', value)}
          />

          <Select
            label="Monitor Vendors"
            options={vendorOptions}
            value={alertData?.vendors}
            onChange={(value) => handleInputChange('vendors', value)}
          />

          {/* Alert Info */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <div className="flex items-start space-x-2">
              <Icon name="Info" size={16} color="var(--color-accent)" className="mt-0.5" />
              <div className="text-sm">
                <p className="text-foreground font-medium mb-1">How it works:</p>
                <ul className="text-muted-foreground space-y-1">
                  <li>• We'll monitor prices across all vendors</li>
                  <li>• You'll get notified when price drops to your target</li>
                  <li>• Alerts include direct purchase links</li>
                  <li>• Cancel anytime from your dashboard</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              className="flex-1"
              loading={isSubmitting}
              iconName="Bell"
              iconPosition="left"
            >
              Set Alert
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PriceAlertModal;